import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { FolderOpen, Plus, Eye, EyeOff, Edit, Calendar, Mail, Phone, MapPin, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { Link } from 'react-router-dom';

export const ProfessionalDossiersPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const [showForm, setShowForm] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState<string>('');
  const queryClient = useQueryClient();

  // Get screening data to check if user is a vertrouwenspersoon
  const { data: screeningData } = useQuery({
    queryKey: ['screening-status'],
    queryFn: async () => {
      const response = await api.get('/screening/status');
      return response.data;
    },
  });

  // Check if user is a vertrouwenspersoon
  const isVertrouwenspersoon = useMemo(() => {
    if (!screeningData?.data?.screening?.selected_professional_types) return false;
    try {
      const types = typeof screeningData.data.screening.selected_professional_types === 'string'
        ? JSON.parse(screeningData.data.screening.selected_professional_types)
        : screeningData.data.screening.selected_professional_types;
      return Array.isArray(types) && types.includes('vertrouwenspersoon');
    } catch {
      return false;
    }
  }, [screeningData]);

  // Get active employers
  const { data: employers } = useQuery({
    queryKey: ['active-employers'],
    queryFn: async () => {
      const response = await api.get('/professional/active-employers');
      return response.data.data || [];
    }
  });

  // Get dossiers
  const { data: dossiers, isLoading } = useQuery({
    queryKey: ['dossiers', selectedEmployer],
    queryFn: async () => {
      const params: any = {};
      if (selectedEmployer) params.employer_id = selectedEmployer;
      const response = await api.get('/professional/dossiers', { params });
      return response.data.data || [];
    }
  });

  // Create dossier mutation (only for vertrouwenspersoon)
  const createDossier = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/professional/dossiers/vertrouwenspersoon', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('dossiers.created'));
      queryClient.invalidateQueries({ queryKey: ['dossiers'] });
      setShowForm(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('dossiers.createError'));
    }
  });

  const updateDossier = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.patch(`/professional/dossiers/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('dossiers.updated'));
      queryClient.invalidateQueries({ queryKey: ['dossiers'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('dossiers.updateError'));
    }
  });

  const groupedDossiers = dossiers?.reduce((acc: any, dossier: any) => {
    const employerId = dossier.employer_id;
    if (!acc[employerId]) {
      acc[employerId] = {
        employer: {
          id: dossier.employer_id,
          name: dossier.employer_first_name + ' ' + dossier.employer_last_name,
          company: dossier.employer_company
        },
        dossiers: []
      };
    }
    acc[employerId].dossiers.push(dossier);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dossiers.title')}</h1>
          <p className="mt-1 text-gray-600">{t('dossiers.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg"
            value={selectedEmployer}
            onChange={(e) => setSelectedEmployer(e.target.value)}
          >
            <option value="">{t('dossiers.allEmployers')}</option>
            {employers?.map((emp: any) => (
              <option key={emp.employer_user_id} value={emp.employer_user_id}>
                {emp.employer_first_name} {emp.employer_last_name}
              </option>
            ))}
          </select>
          {isVertrouwenspersoon && (
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('dossiers.newDossier')}
            </Button>
          )}
        </div>
      </div>


      {isLoading ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600">{t('dossiers.loadingText')}</p>
          </div>
        </Card>
      ) : groupedDossiers && Object.keys(groupedDossiers).length > 0 ? (
        Object.values(groupedDossiers).map((group: any) => (
          <Card key={group.employer.id} className="mb-6">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {group.employer.name}
                {group.employer.company && (
                  <span className="text-gray-500 text-base font-normal ml-2">
                    ({group.employer.company})
                  </span>
                )}
              </h2>
              <div className="space-y-4">
                {group.dossiers.map((dossier: any) => (
                  <DossierCard
                    key={dossier.id}
                    dossier={dossier}
                    onUpdate={(data) => updateDossier.mutate({ id: dossier.id, data })}
                  />
                ))}
              </div>
            </div>
          </Card>
        ))
      ) : (
        <Card>
          <div className="text-center py-12">
            <FolderOpen className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('dossiers.noDossiers')}
            </h2>
            {isVertrouwenspersoon && (
              <p className="text-gray-600 mb-4">
                {t('dossiers.noDossiersVp')}
              </p>
            )}
            {isVertrouwenspersoon && (
              <Button onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('dossiers.createNewDossier')}
              </Button>
            )}
          </div>
        </Card>
      )}

      {showForm && isVertrouwenspersoon && (
        <DossierForm
          employers={employers || []}
          onSubmit={(data) => createDossier.mutate(data)}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

const DossierForm: React.FC<{
  employers: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ employers, onSubmit, onCancel }) => {
  const { t } = useTranslation('professional');
  const [formData, setFormData] = useState({
    employer_id: '',
    client_name: '',
    client_address: '',
    client_email: '',
    client_phone: '',
    status: 'nieuwe_aanmelding',
    description: '',
    is_anonymous: false,
    visible_to_employer: false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.client_name.trim()) {
      toast.error(t('dossiers.clientNameRequired'));
      return;
    }
    onSubmit(formData);
  };

  return (
    <Card className="mb-6">
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('dossiers.createNewDossier')}</h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('dossiers.vpCreateDescription')}
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dossiers.employerOptionalAnon')}
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.employer_id}
            onChange={(e) => setFormData({ ...formData, employer_id: e.target.value })}
          >
            <option value="">{t('dossiers.noEmployerAnon')}</option>
            {employers.map((emp: any) => (
              <option key={emp.employer_user_id} value={emp.employer_user_id}>
                {emp.employer_first_name} {emp.employer_last_name}
                {emp.employer_company && ` - ${emp.employer_company}`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dossiers.clientNameLabel')}
          </label>
          <input
            type="text"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.client_name}
            onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dossiers.addressOptional')}
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.client_address}
            onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dossiers.emailOptional')}
          </label>
          <input
            type="email"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.client_email}
            onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dossiers.phoneOptional')}
          </label>
          <input
            type="tel"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={formData.client_phone}
            onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('dossiers.descriptionOptional')}
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_anonymous"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
            checked={formData.is_anonymous}
            onChange={(e) => setFormData({ ...formData, is_anonymous: e.target.checked })}
          />
          <label htmlFor="is_anonymous" className="text-sm text-gray-700">
            {t('dossiers.anonymousReport')}
          </label>
        </div>

        {formData.employer_id && (
          <div className="flex items-center">
            <input
              type="checkbox"
              id="visible_to_employer"
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
              checked={formData.visible_to_employer}
              onChange={(e) => setFormData({ ...formData, visible_to_employer: e.target.checked })}
            />
            <label htmlFor="visible_to_employer" className="text-sm text-gray-700">
              {t('dossiers.visibleToEmployer')}
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" type="button" onClick={onCancel}>
            {t('dossiers.cancel')}
          </Button>
          <Button type="submit">
            {t('dossiers.createDossier')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

const DossierCard: React.FC<{
  dossier: any;
  onUpdate: (data: any) => void;
}> = ({ dossier, onUpdate }) => {
  const { t } = useTranslation('professional');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    status: dossier.status,
    client_name: dossier.client_name,
    client_address: dossier.client_address || '',
    client_email: dossier.client_email || '',
    client_phone: dossier.client_phone || '',
    visible_to_employer: dossier.visible_to_employer
  });

  const handleSave = () => {
    onUpdate(formData);
    setIsEditing(false);
  };

  const statusLabel = t(`status.${dossier.status}`, dossier.status) as string;
  const conversationCount = Number(dossier.conversation_count || 0);
  const maxConversations =
    dossier.max_conversations !== null && dossier.max_conversations !== undefined
      ? Number(dossier.max_conversations)
      : null;
  const approvalRequired =
    dossier.approval_required === true || (maxConversations !== null ? conversationCount >= maxConversations : false);

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-medium text-gray-900">{dossier.client_name}</h3>
            <span className={`px-2 py-1 text-xs rounded-full ${
              dossier.status === 'lopend' ? 'bg-green-100 text-green-800' :
              dossier.status === 'afsluiten' ? 'bg-gray-100 text-gray-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {statusLabel}
            </span>
            {approvalRequired && (
              <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
                {t('dossiers.approvalNeeded')}
              </span>
            )}
            {dossier.visible_to_employer ? (
              <Eye className="w-4 h-4 text-green-600" {...({ title: t('dossiers.visibleToEmployer') } as any)} />
            ) : (
              <EyeOff className="w-4 h-4 text-gray-400" {...({ title: t('dossiers.notVisibleToEmployer') } as any)} />
            )}
          </div>
          {maxConversations !== null && (
            <div className="text-xs text-gray-600 mb-2">
              {t('dossiers.conversations')}: {conversationCount}/{maxConversations}
            </div>
          )}
          {dossier.client_address && (
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <MapPin className="w-4 h-4 mr-1" />
              {dossier.client_address}
            </div>
          )}
          {dossier.client_email && (
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <Mail className="w-4 h-4 mr-1" />
              {dossier.client_email}
            </div>
          )}
          {dossier.client_phone && (
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <Phone className="w-4 h-4 mr-1" />
              {dossier.client_phone}
            </div>
          )}
          <div className="flex items-center text-sm text-gray-500 mt-2">
            <Calendar className="w-4 h-4 mr-1" />
            {t('dossiers.started')}: {new Date(dossier.start_date).toLocaleDateString('nl-NL')}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/professional/dossiers/${dossier.id}/verslagen/nieuw`}>
            <Button
              variant="outline"
              size="sm"
            >
              <FileText className="w-4 h-4 mr-1" />
              {t('cockpit.newReport')}
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
          >
            <Edit className="w-4 h-4 mr-1" />
            {isEditing ? t('dossiers.cancel') : t('dossiers.edit')}
          </Button>
        </div>
      </div>

      {isEditing && (
        <div className="mt-4 space-y-3 border-t pt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('table.status')}
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              {[
                'nieuwe_aanmelding', 'intake_ingepland', 'eerste_gesprek_ingepland',
                'tweede_gesprek_ingepland', 'lopend', 'eindgesprek', 'afsluiten', 'archived', 'on_hold'
              ].map(value => (
                <option key={value} value={value}>{t(`status.${value}`, value)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('dossiers.clientName')}
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('dossiers.addressOptional')}
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.client_address}
              onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('dossiers.emailOptional')}
            </label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.client_email}
              onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('dossiers.phoneOptional')}
            </label>
            <input
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={formData.client_phone}
              onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id={`visible-${dossier.id}`}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
              checked={formData.visible_to_employer}
              onChange={(e) => setFormData({ ...formData, visible_to_employer: e.target.checked })}
            />
            <label htmlFor={`visible-${dossier.id}`} className="text-sm text-gray-700">
              {t('dossiers.visibleToEmployer')}
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              {t('dossiers.cancel')}
            </Button>
            <Button onClick={handleSave}>
              {t('dossiers.save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
